import { isToday, isYesterday, format } from 'date-fns';
import { resolveNotificationRoute } from '@/common/utils/notificationRouter';

/**
 * Safely parse and merge notification payload, data, and metadata.
 */
export function parseNotificationPayload(notification) {
  if (!notification || typeof notification !== 'object') return {};

  let payload = notification.payload;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      payload = {};
    }
  }
  if (!payload || typeof payload !== 'object') {
    payload = {};
  }

  let extraData =
    notification.data || notification.metadata || notification.extra_data || {};
  if (typeof extraData === 'string') {
    try {
      extraData = JSON.parse(extraData);
    } catch {
      extraData = {};
    }
  }
  if (!extraData || typeof extraData !== 'object') {
    extraData = {};
  }

  return { ...extraData, ...payload };
}

/**
 * Extract notification type and channel configuration for icon and color.
 * Requirement 1: Render notifications according to their type
 * - WhatsApp messages: WhatsApp/message icon & subtle color
 * - Lead stage updates: lead/stage icon
 * - Task notifications: task icon
 * - Unknown types: fall back to bell icon and API title/message
 */
export function getNotificationTypeConfig(notification) {
  const payload = parseNotificationPayload(notification);
  const rawType =
    notification?.notification_type ||
    payload.notification_type ||
    notification?.type ||
    payload.type ||
    payload.event ||
    '';

  const normalizedType = String(rawType)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_');

  const channel = String(payload.channel || notification?.channel || '')
    .trim()
    .toLowerCase();

  // WhatsApp / Messages / Chat
  const isMessage =
    channel === 'whatsapp' ||
    channel === 'instagram' ||
    [
      'inbox_message',
      'whatsapp_message',
      'chat_message',
      'message_received',
      'new_message',
      'incoming_message',
      'whatsapp_chat',
      'chat',
      'message',
      'inbox',
    ].includes(normalizedType) ||
    normalizedType.includes('message') ||
    normalizedType.includes('chat');

  if (isMessage) {
    if (channel === 'instagram') {
      return {
        category: 'message',
        iconType: 'instagram',
        bgClass: 'bg-pink-50 text-pink-500',
        badgeBg: 'bg-pink-50 text-pink-700 border-pink-200',
        channelLabel: 'Instagram',
      };
    }
    return {
      category: 'message',
      iconType: 'whatsapp',
      bgClass: 'bg-[#E8F8EE] text-[#25D366]',
      badgeBg: 'bg-[#EBFBF0] text-[#128C7E] border-[#A7E8BD]',
      channelLabel: channel
        ? channel.charAt(0).toUpperCase() + channel.slice(1)
        : 'WhatsApp',
    };
  }

  // Lead / Stage / CRM Contact updates
  const isLeadOrStage =
    normalizedType.includes('stage') ||
    normalizedType.includes('lead') ||
    normalizedType.includes('contact') ||
    normalizedType.includes('client') ||
    normalizedType === 'crm_activity';

  if (isLeadOrStage) {
    return {
      category: 'lead_stage',
      iconType: normalizedType.includes('stage') ? 'stage' : 'lead',
      bgClass: 'bg-indigo-50 text-indigo-500',
      badgeBg: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      typeLabel: normalizedType.includes('stage') ? 'Stage' : 'Lead',
    };
  }

  // Task / Reminder notifications
  const isTask =
    normalizedType.includes('task') ||
    normalizedType.includes('reminder') ||
    normalizedType.includes('import_reminder');

  if (isTask) {
    return {
      category: 'task',
      iconType: 'task',
      bgClass: 'bg-blue-50 text-blue-500',
      badgeBg: 'bg-blue-50 text-blue-700 border-blue-200',
      typeLabel: normalizedType.includes('reminder') ? 'Reminder' : 'Task',
    };
  }

  // Integrations (Google Workspace, Meta, Webhooks, etc.)
  const isIntegration =
    normalizedType.includes('integration') ||
    normalizedType.includes('workspace') ||
    normalizedType.includes('token_expired') ||
    normalizedType.includes('authentication_failed') ||
    normalizedType.includes('permissions_revoked') ||
    normalizedType.includes('reconnect') ||
    normalizedType.includes('unhealthy') ||
    normalizedType.includes('disconnected') ||
    normalizedType.includes('connected');

  if (isIntegration) {
    return {
      category: 'integration',
      iconType: 'integration',
      bgClass: 'bg-[#FFF5EB] text-[#F97316]',
      badgeBg: 'bg-amber-50 text-amber-700 border-amber-200',
      typeLabel: 'Integration',
    };
  }

  // Campaigns
  if (normalizedType.includes('campaign')) {
    return {
      category: 'campaign',
      iconType: 'campaign',
      bgClass: 'bg-teal-500/10 text-teal-600',
      badgeBg: 'bg-teal-50 text-teal-700 border-teal-200',
      typeLabel: 'Campaign',
    };
  }

  // Appointments / Calendar
  if (
    normalizedType.includes('appointment') ||
    normalizedType.includes('booking') ||
    normalizedType.includes('calendar')
  ) {
    return {
      category: 'appointment',
      iconType: 'calendar',
      bgClass: 'bg-purple-500/10 text-purple-600',
      badgeBg: 'bg-purple-50 text-purple-700 border-purple-200',
      typeLabel: 'Appointment',
    };
  }

  // Quotation / Order
  if (
    normalizedType.includes('quotation') ||
    normalizedType.includes('order')
  ) {
    return {
      category: 'quotation',
      iconType: 'quotation',
      bgClass: 'bg-emerald-500/10 text-emerald-700',
      badgeBg: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      typeLabel: 'Quotation',
    };
  }

  // Fallback unknown type
  return {
    category: 'unknown',
    iconType: 'bell',
    bgClass: 'bg-slate-500/10 text-slate-600',
    badgeBg: 'bg-slate-50 text-slate-700 border-slate-200',
    typeLabel: 'Notification',
  };
}

/**
 * Requirement 2: Improve message notification content
 * - For inbox_message, display payload.sender_name as main heading
 * - Show channel as small label, payload.preview as message preview
 * - Fall back to title/message when missing
 * - Avoid repeating sender name in preview
 */
export function getMessageNotificationContent(notification) {
  const payload = parseNotificationPayload(notification);
  const typeConfig = getNotificationTypeConfig(notification);
  const isMessage = typeConfig.category === 'message';

  const senderName =
    payload.sender_name ||
    payload.senderName ||
    payload.sender ||
    (isMessage ? notification?.title : null);

  const rawHeading =
    senderName || notification?.title || payload.title || 'Notification';

  // Preview extraction
  let rawPreview =
    payload.preview ||
    payload.message ||
    payload.body ||
    notification?.message ||
    notification?.body ||
    '';

  // Avoid repeating sender name in preview (e.g., "Dharmik Prajapati: Test inbox notification")
  let cleanPreview = String(rawPreview || '').trim();
  if (senderName && typeof senderName === 'string') {
    const trimmedSender = senderName.trim();
    if (trimmedSender) {
      const escaped = trimmedSender.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const prefixRegex = new RegExp(`^${escaped}\\s*[:\\-]\\s*`, 'i');
      cleanPreview = cleanPreview.replace(prefixRegex, '').trim();
    }
  }

  // If preview became empty, fall back to API message or title
  if (!cleanPreview) {
    cleanPreview = notification?.message || notification?.title || '';
  }

  return {
    heading: rawHeading,
    title: rawHeading,
    preview: cleanPreview,
    subtext: cleanPreview,
    message: cleanPreview,
    channelLabel: typeConfig.channelLabel || null,
    channelBadge: typeConfig.channelLabel || null,
    isMessage,
  };
}

/**
 * Requirement 6: Show task details only when applicable
 * - Display task_status_label as compact status badge when present
 * - Display progress only when progress_total > 0 and valid progress values exist
 * - Hide null fields and meaningless values such as 0/0
 */
export function getTaskDetails(notification) {
  const payload = parseNotificationPayload(notification);

  const statusLabel =
    notification?.task_status_label ||
    payload.task_status_label ||
    payload.taskStatusLabel ||
    payload.status_label ||
    payload.task_status ||
    null;

  const rawTotal =
    notification?.progress_total ??
    payload.progress_total ??
    payload.progressTotal ??
    payload.total;

  const rawCurrent =
    notification?.progress_current ??
    payload.progress_current ??
    payload.progressCurrent ??
    payload.progress_completed ??
    payload.progress ??
    payload.current;

  let progress = null;
  if (
    rawTotal !== undefined &&
    rawTotal !== null &&
    rawCurrent !== undefined &&
    rawCurrent !== null
  ) {
    const total = Number(rawTotal);
    const current = Number(rawCurrent);
    if (!isNaN(total) && total > 0 && !isNaN(current) && current >= 0) {
      progress = {
        current,
        total,
        text: `${current}/${total}`,
        percentage: Math.min(100, Math.round((current / total) * 100)),
      };
    }
  }

  return {
    statusLabel: statusLabel ? String(statusLabel).trim() : null,
    progress,
  };
}

/**
 * Requirement 5: Add relevant navigation
 * - Inspect route, action_url, redirect_url, and payload equivalents
 * - Prefer valid internal route
 * - Provide clear action labels ("Open chat", "View lead", "View task", etc.)
 */
export function getNotificationRouteInfo(notification) {
  if (!notification)
    return { path: null, actionLabel: null, isNavigable: false };

  let resolvedPath = null;
  try {
    resolvedPath = resolveNotificationRoute(notification);
  } catch {
    resolvedPath = null;
  }

  if (
    !resolvedPath ||
    typeof resolvedPath !== 'string' ||
    !resolvedPath.startsWith('/')
  ) {
    return { path: null, actionLabel: null, isNavigable: false };
  }

  const cleanPath = resolvedPath.split('?')[0];

  // Pick an explicit action label matching the route destination
  let actionLabel = null;
  if (cleanPath.startsWith('/whatsapp-chat')) {
    actionLabel = 'Open chat';
  } else if (
    cleanPath.startsWith('/crm-contacts') ||
    cleanPath.startsWith('/crm-pipeline')
  ) {
    actionLabel = 'View lead';
  } else if (cleanPath.startsWith('/reminders')) {
    actionLabel = 'View task';
  } else if (cleanPath.startsWith('/integrations')) {
    actionLabel = 'View integration';
  } else if (cleanPath.startsWith('/campaigns')) {
    actionLabel = 'View campaign';
  } else if (cleanPath.startsWith('/calendar')) {
    actionLabel = 'View calendar';
  } else if (cleanPath.startsWith('/quotations')) {
    actionLabel = 'View quotation';
  } else if (cleanPath !== '/dashboard') {
    actionLabel = 'View details';
  }

  return {
    path: resolvedPath,
    actionLabel,
    isNavigable: cleanPath !== '/dashboard' || Boolean(actionLabel),
  };
}

/**
 * Format relative timestamp and full local date/time for hover tooltip.
 * Requirement 7: Relative timestamps and full local date/time on hover
 */
export function formatNotificationTime(dateStr) {
  if (!dateStr) return { relative: '', full: '' };

  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return { relative: '', full: '' };

    const full = format(d, 'EEEE, MMMM d, yyyy h:mm a');

    const diffMs = Date.now() - d.getTime();
    if (diffMs < 0) {
      return { relative: 'Just now', full };
    }

    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return { relative: 'Just now', full };
    if (diffMins < 60) return { relative: `${diffMins}m ago`, full };

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return { relative: `${diffHours}h ago`, full };

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return { relative: 'Yesterday', full };
    if (diffDays < 7) return { relative: `${diffDays}d ago`, full };

    let display = '';
    if (isToday(d) || isYesterday(d)) {
      display = format(d, 'h:mm a');
    } else {
      display = format(d, 'd MMM');
    }

    return {
      relative: format(d, 'MMM d'),
      full,
      display,
      tooltip: full,
    };
  } catch {
    return { relative: '', full: '', display: '', tooltip: '' };
  }
}

/**
 * Requirement 4: Group unread messages from the same conversation
 * - Group only unread inbox_message notifications with the same conversation ID and channel
 * - Support payload.conversation_id and payload.conversationId
 * - Show latest message preview, latest timestamp, and “N new messages” badge
 * - Retain all underlying notification IDs for existing read actions
 * - Do not group unrelated notification types
 * - Do not imply loaded-page count is server-wide total unread
 */
export function groupUnreadNotifications(
  notifications = [],
  localReadIds = new Set(),
) {
  if (!Array.isArray(notifications) || notifications.length === 0) return [];

  const isUnread = (item) => !item.read_at && !localReadIds.has(item.id);

  // Map to collect unread inbox message groups: key -> { latest, notificationIds: [], count }
  const conversationGroups = new Map();

  for (const item of notifications) {
    if (!item || !item.id) continue;

    const payload = parseNotificationPayload(item);
    const rawType =
      item.notification_type ||
      payload.notification_type ||
      item.type ||
      payload.type ||
      payload.event ||
      '';
    const normalizedType = String(rawType)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_');

    const isInbox =
      normalizedType === 'inbox_message' ||
      normalizedType.startsWith('inbox_message') ||
      normalizedType === 'whatsapp_message';

    const convId =
      payload.conversation_id ||
      payload.conversationId ||
      payload.chat_id ||
      payload.chatId;

    const channel = String(payload.channel || 'whatsapp')
      .toLowerCase()
      .trim();

    if (isUnread(item) && isInbox && convId) {
      const groupKey = `${channel}_${convId}`;
      if (!conversationGroups.has(groupKey)) {
        conversationGroups.set(groupKey, {
          latest: item,
          notificationIds: [item.id],
          count: 1,
        });
      } else {
        const group = conversationGroups.get(groupKey);
        group.notificationIds.push(item.id);
        group.count += 1;

        const itemTime = item.created_at
          ? new Date(item.created_at).getTime()
          : 0;
        const latestTime = group.latest.created_at
          ? new Date(group.latest.created_at).getTime()
          : 0;

        if (itemTime > latestTime) {
          group.latest = item;
        }
      }
    }
  }

  // Produce list with grouped unread messages represented by single item
  const seenGroupKeys = new Set();
  const groupedResult = [];

  for (const item of notifications) {
    if (!item || !item.id) continue;

    const payload = parseNotificationPayload(item);
    const rawType =
      item.notification_type ||
      payload.notification_type ||
      item.type ||
      payload.type ||
      payload.event ||
      '';
    const normalizedType = String(rawType)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_');

    const isInbox =
      normalizedType === 'inbox_message' ||
      normalizedType.startsWith('inbox_message') ||
      normalizedType === 'whatsapp_message';

    const convId =
      payload.conversation_id ||
      payload.conversationId ||
      payload.chat_id ||
      payload.chatId;

    const channel = String(payload.channel || 'whatsapp')
      .toLowerCase()
      .trim();

    if (isUnread(item) && isInbox && convId) {
      const groupKey = `${channel}_${convId}`;
      if (seenGroupKeys.has(groupKey)) {
        // Skip older messages already grouped into this conversation
        continue;
      }
      seenGroupKeys.add(groupKey);
      const group = conversationGroups.get(groupKey);

      groupedResult.push({
        ...group.latest,
        _isGrouped: group.count > 1,
        _groupCount: group.count,
        _notificationIds: group.notificationIds,
      });
    } else {
      groupedResult.push({
        ...item,
        _isGrouped: false,
        _groupCount: 1,
        _notificationIds: [item.id],
      });
    }
  }

  return groupedResult;
}

/**
 * Requirement 7: Group notifications under Today, Yesterday, and Earlier using user's local timezone.
 * Sort by created_at, newest first.
 */
export function groupNotificationsByDate(notifications = []) {
  if (!Array.isArray(notifications) || notifications.length === 0) {
    return { today: [], yesterday: [], earlier: [] };
  }

  const sorted = [...notifications].sort((a, b) => {
    const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return timeB - timeA;
  });

  const today = [];
  const yesterday = [];
  const earlier = [];

  for (const item of sorted) {
    if (!item.created_at) {
      earlier.push(item);
      continue;
    }

    try {
      const d = new Date(item.created_at);
      if (isNaN(d.getTime())) {
        earlier.push(item);
      } else if (isToday(d)) {
        today.push(item);
      } else if (isYesterday(d)) {
        yesterday.push(item);
      } else {
        earlier.push(item);
      }
    } catch {
      earlier.push(item);
    }
  }

  return { today, yesterday, earlier };
}
